/**
 * The new Grid's day strip (#1766, threads/new-grid/mockup.html panel 1):
 * one colored bar for how the day is going, and one "Needs you" row for every
 * signal that used to be its own banner (online bookings, no card on file,
 * missed visits, running late, no time, no tech, not on the map, past due).
 *
 * Nothing is hidden: each banner became a chip with its count, and the chip
 * opens the same place the banner did. Orange only where a person must act;
 * facts that are not today's to-do (past due, a free helper) are grey.
 */
import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { HelpCircle } from "lucide-react";
import { api } from "@/lib/api";
import { live, LIVE_SLOW_MS } from "@/lib/live-query";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { HELPER_DRAG_MIME, type HelperChipInfo } from "../helper-chips";
import { CARD_STATE_LABEL, STATE_ORDER, initials, type CardState } from "./card-state";
import { PERSON_INK, STATE_SWATCH_CLASS } from "./state-style";

export interface NeedsYou {
    missed: number;
    late: number;
    noTime: number;
    noTech: number;
    notOnMap: number;
    pastDue: number;
}

interface Props {
    tally: Record<CardState, number>;
    techsWorking: number;
    techsTotal: number;
    helperCount: number;
    needs: NeedsYou;
    freeHelpers: HelperChipInfo[];
    draggingHelperId: string | null;
    onHelperDragStart: (helperId: string) => void;
    onHelperDragEnd: () => void;
    /** Dropped on "Work solo": a helper works on their own today; a full
     *  tech who was made a helper gets his own row back (shell decides). */
    onBenchDrop: (helperId: string) => void;
    /** ✕ on a free chip that is free today only: undo today's pairing row. */
    onHelperRevert?: (helper: HelperChipInfo) => void;
    /** Opens the triage detail (missed, running late, not on the map). */
    onOpenTriage: () => void;
    onJumpToJob: (kind: "noTime" | "noTech") => void;
}

function Chip({
    n,
    children,
    calm,
    onClick,
    to,
    tip,
    testId,
}: {
    n: number;
    children: ReactNode;
    calm?: boolean;
    onClick?: () => void;
    to?: string;
    tip?: string;
    testId?: string;
}) {
    const cls = cn(
        "inline-flex items-center gap-1.5 rounded-full py-1 pl-1 pr-2.5 text-[13px] font-semibold transition-colors",
        calm
            ? "bg-secondary text-secondary-foreground hover:bg-stone-200 dark:hover:bg-stone-700"
            : cn("bg-orange-50 hover:bg-orange-100 dark:bg-orange-950 dark:hover:bg-orange-900", PERSON_INK),
    );
    const badge = (
        <span
            className={cn(
                "grid h-5 min-w-5 place-items-center rounded-full px-1.5 text-xs font-bold tabular-nums text-white",
                calm ? "bg-stone-500" : STATE_SWATCH_CLASS.person,
                !calm && "dark:text-orange-950",
            )}
        >
            {n}
        </span>
    );
    if (to) {
        return (
            <Link to={to} className={cls} title={tip} data-testid={testId}>
                {badge}
                {children}
            </Link>
        );
    }
    return (
        <button type="button" className={cls} onClick={onClick} title={tip} data-testid={testId} disabled={!onClick}>
            {badge}
            {children}
        </button>
    );
}

export function NewGridDayStrip(props: Props) {
    const { tally, needs } = props;
    const bookingsQ = useQuery({
        queryKey: ["grid", "pending-bookings-count"],
        ...live(LIVE_SLOW_MS),
        queryFn: () => api.get<{ count: number }>("/api/online-bookings/pending-count"),
        staleTime: 60_000,
        retry: false,
    });
    const cardBlockedQ = useQuery({
        queryKey: ["grid", "card-blocked-count"],
        ...live(LIVE_SLOW_MS),
        queryFn: () => api.get<{ count: number }>("/api/jobs/pre-dispatch-payment"),
        staleTime: 60_000,
        retry: false,
    });
    const [benchHover, setBenchHover] = useState(false);
    const bookings = bookingsQ.data?.count ?? 0;
    const cardBlocked = cardBlockedQ.data?.count ?? 0;
    const total = STATE_ORDER.reduce((s, k) => s + tally[k], 0);
    const anyNeeds =
        bookings + cardBlocked + needs.missed + needs.late + needs.noTime + needs.noTech + needs.notOnMap > 0;

    return (
        <div className="grid gap-3 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]" data-testid="new-grid-day-strip">
            <div className="min-w-0 rounded-2xl border border-border bg-card px-4 py-3 shadow-sm">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span>
                        <span className="text-2xl font-extrabold tabular-nums tracking-tight">{total}</span>{" "}
                        <span className="text-muted-foreground">jobs on the board</span>
                    </span>
                    <span className="text-sm tabular-nums text-muted-foreground">
                        {props.techsWorking} of {props.techsTotal} techs working
                        {props.helperCount > 0
                            ? ` · ${props.helperCount} helper${props.helperCount === 1 ? "" : "s"}`
                            : ""}
                    </span>
                </div>
                <div
                    className="mt-2 flex h-5 gap-0.5 overflow-hidden rounded-full bg-secondary"
                    role="img"
                    aria-label="How the day is going"
                >
                    {STATE_ORDER.filter((k) => tally[k] > 0).map((k) => (
                        <i
                            key={k}
                            className={cn("block h-full", STATE_SWATCH_CLASS[k])}
                            style={{ flex: tally[k] }}
                            title={`${CARD_STATE_LABEL[k]}: ${tally[k]}`}
                        />
                    ))}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px]">
                    {STATE_ORDER.filter((k) => tally[k] > 0).map((k) => (
                        <span key={k} className="inline-flex items-center gap-1.5">
                            <i className={cn("block h-3 w-3 rounded", STATE_SWATCH_CLASS[k])} />
                            <b className="tabular-nums">{tally[k]}</b> {CARD_STATE_LABEL[k].toLowerCase()}
                        </span>
                    ))}
                    <Popover>
                        <PopoverTrigger asChild>
                            <button
                                type="button"
                                className="ml-auto inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                                aria-label="What the colors mean"
                            >
                                <HelpCircle className="h-3.5 w-3.5" /> Key
                            </button>
                        </PopoverTrigger>
                        <PopoverContent align="end" className="w-72 space-y-2 text-sm">
                            {(["todo", "there", "finished", "office", "person"] as CardState[]).map((k) => (
                                <div key={k} className="flex items-center gap-2">
                                    <i className={cn("block h-3 w-3 shrink-0 rounded", STATE_SWATCH_CLASS[k])} />
                                    {CARD_STATE_LABEL[k]}
                                </div>
                            ))}
                            <div className="flex items-center gap-2">
                                <span className="grid h-4 w-4 place-items-center rounded-full bg-red-600 text-[10px] font-black text-white">
                                    !
                                </span>
                                Past its due date
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="grid h-4 w-4 shrink-0 place-items-center rounded-full bg-amber-100 text-[8px] font-extrabold text-amber-900 dark:bg-amber-900 dark:text-amber-100">
                                    MK
                                </span>
                                Helper who rides with this tech every day
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="grid h-4 w-4 shrink-0 place-items-center rounded-full border-2 border-dashed border-amber-600 bg-card text-[8px] font-extrabold text-amber-800 dark:border-amber-400 dark:text-amber-200">
                                    MK
                                </span>
                                With this tech today only. ✕ undoes it.
                            </div>
                            <p className="text-xs text-muted-foreground">
                                Blocks on a card are devices. Width is how long it takes. The ring around a tech is how
                                full their day is; green is what's done. Hover any card for its numbers.
                            </p>
                        </PopoverContent>
                    </Popover>
                </div>
            </div>
            <div className="min-w-0 rounded-2xl border border-border bg-card px-4 py-3 shadow-sm">
                <div className="flex items-center justify-between gap-2">
                    <span
                        className={cn(
                            "text-[11px] font-bold uppercase tracking-[0.08em]",
                            anyNeeds ? PERSON_INK : "text-muted-foreground",
                        )}
                    >
                        Needs you
                    </span>
                    {!anyNeeds && <span className="text-xs text-muted-foreground">Nothing waiting on a person</span>}
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                    {bookings > 0 && (
                        <Chip
                            n={bookings}
                            to="/booking"
                            tip="New online bookings from the website"
                            testId="needs-bookings"
                        >
                            booking{bookings === 1 ? "" : "s"} to review
                        </Chip>
                    )}
                    {cardBlocked > 0 && (
                        <Chip
                            n={cardBlocked}
                            to="/jobs/pre-dispatch-payment"
                            tip="Can't dispatch until a card is on file"
                        >
                            no card on file
                        </Chip>
                    )}
                    {needs.missed > 0 && (
                        <Chip
                            n={needs.missed}
                            onClick={props.onOpenTriage}
                            tip="Visits that were scheduled and never done"
                        >
                            missed
                        </Chip>
                    )}
                    {needs.late > 0 && (
                        <Chip n={needs.late} onClick={props.onOpenTriage} tip="Running long or late to start">
                            running late
                        </Chip>
                    )}
                    {needs.noTime > 0 && (
                        <Chip
                            n={needs.noTime}
                            onClick={() => props.onJumpToJob("noTime")}
                            tip="On a tech with no time — orange at the end of their lane"
                        >
                            job{needs.noTime === 1 ? " has" : "s have"} no time
                        </Chip>
                    )}
                    {needs.noTech > 0 && (
                        <Chip
                            n={needs.noTech}
                            onClick={() => props.onJumpToJob("noTech")}
                            tip="A time but no tech — the top lane"
                        >
                            no tech yet
                        </Chip>
                    )}
                    {needs.notOnMap > 0 && (
                        <Chip
                            n={needs.notOnMap}
                            onClick={props.onOpenTriage}
                            tip="Site has no coordinates — backfill them"
                        >
                            not on the map
                        </Chip>
                    )}
                    {needs.pastDue > 0 && (
                        <Chip n={needs.pastDue} calm tip="Visits after the due date. Red ! on the card.">
                            past due
                        </Chip>
                    )}
                </div>
                {(props.helperCount > 0 || props.freeHelpers.length > 0 || props.draggingHelperId) && (
                    <div
                        className={cn(
                            "mt-3 flex flex-wrap items-center gap-2 rounded-xl border-2 border-dashed border-stone-300 px-3 py-2 transition-colors dark:border-stone-600",
                            props.draggingHelperId &&
                                "border-sky-500 bg-sky-50/60 dark:border-sky-400 dark:bg-sky-950/40",
                            benchHover && "border-sky-600 bg-sky-100 dark:bg-sky-900/60",
                        )}
                        data-testid="new-grid-work-solo"
                        onDragOver={(e) => {
                            if (!e.dataTransfer.types.includes(HELPER_DRAG_MIME)) return;
                            e.preventDefault();
                            e.dataTransfer.dropEffect = "move";
                            setBenchHover(true);
                        }}
                        onDragLeave={() => setBenchHover(false)}
                        onDrop={(e) => {
                            setBenchHover(false);
                            const id = e.dataTransfer.getData(HELPER_DRAG_MIME);
                            if (!id) return;
                            e.preventDefault();
                            props.onBenchDrop(id);
                        }}
                    >
                        <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
                            Work solo
                        </span>
                        <span className="text-xs text-muted-foreground">
                            {props.draggingHelperId
                                ? "Drop here: they work on their own today. A tech gets his own row back."
                                : props.freeHelpers.length === 0
                                  ? "Drag a helper here to have them work on their own today."
                                  : ""}
                        </span>
                        {props.freeHelpers.map((h) => (
                            <span
                                key={h.id}
                                draggable
                                onDragStart={(e) => {
                                    e.dataTransfer.setData(HELPER_DRAG_MIME, h.id);
                                    e.dataTransfer.effectAllowed = "move";
                                    props.onHelperDragStart(h.id);
                                }}
                                onDragEnd={props.onHelperDragEnd}
                                className="inline-flex cursor-grab items-center gap-1.5 rounded-full bg-secondary py-1 pl-1 pr-2.5 text-[13px] font-semibold text-secondary-foreground active:cursor-grabbing"
                                title={`${h.name} is working on their own today. Drag onto a tech's name to pair them.`}
                                data-testid="new-grid-free-helper"
                            >
                                <span className="grid h-5 w-5 place-items-center rounded-full bg-amber-100 text-[9px] font-extrabold text-amber-900 dark:bg-amber-900 dark:text-amber-100">
                                    {initials(h.name.split(" ")[0], h.name.split(" ").slice(-1)[0])}
                                </span>
                                {h.name.split(" ")[0]}
                                {h.source === "override" && props.onHelperRevert && (
                                    <button
                                        type="button"
                                        draggable={false}
                                        onClick={() => props.onHelperRevert?.(h)}
                                        className="-mr-1 ml-0.5 grid h-4 w-4 place-items-center rounded-full text-[10px] font-black text-muted-foreground hover:bg-red-600 hover:text-white"
                                        title={
                                            h.defaultLeadName
                                                ? `Undo: send ${h.name} back to ${h.defaultLeadName}`
                                                : `Undo today's change for ${h.name}`
                                        }
                                        aria-label={
                                            h.defaultLeadName
                                                ? `Undo: send ${h.name} back to ${h.defaultLeadName}`
                                                : `Undo today's change for ${h.name}`
                                        }
                                        data-testid="new-grid-free-helper-undo"
                                    >
                                        ✕
                                    </button>
                                )}
                            </span>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
